import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Search, Loader2, Table2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DatasheetsPage() {
  const { currentCompany } = useCompany();
  const navigate = useNavigate();
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetName, setSheetName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (currentCompany) fetchDatasheets();
  }, [currentCompany]);

  const fetchDatasheets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/datasheets`, {
        params: { company_id: currentCompany.id },
        withCredentials: true
      });
      setDatasheets(res.data);
    } catch {
      toast.error('Failed to load datasheets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!sheetName.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/datasheets`, {
        company_id: currentCompany.id,
        name: sheetName.trim()
      }, { withCredentials: true });
      toast.success('Datasheet created');
      setIsCreateOpen(false);
      setSheetName('');
      navigate(`/datasheets/${res.data.id}`);
    } catch {
      toast.error('Failed to create datasheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRename = async () => {
    if (!sheetName.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/datasheets/${selectedSheet.id}`, { name: sheetName.trim() }, { withCredentials: true });
      toast.success('Datasheet renamed');
      setIsRenameOpen(false);
      setSelectedSheet(null);
      setSheetName('');
      fetchDatasheets();
    } catch {
      toast.error('Failed to rename datasheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/datasheets/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Datasheet deleted');
      setDeleteTarget(null);
      fetchDatasheets();
    } catch {
      toast.error('Failed to delete datasheet');
    }
  };

  const filtered = datasheets.filter(d =>
    !search || d.name.toLowerCase().includes(search.toLowerCase())
  );

  if (!currentCompany) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="datasheets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Datasheets</h1>
          <p className="text-gray-500 mt-1">Create and manage spreadsheet-style data</p>
        </div>
        <Button onClick={() => { setSheetName(''); setIsCreateOpen(true); }} data-testid="create-datasheet-btn">
          <Plus className="w-4 h-4 mr-2" />New Datasheet
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Search datasheets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-datasheets" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Table2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{search ? 'No datasheets match your search' : 'No datasheets yet'}</h3>
          <p className="text-gray-500 mb-4">Create your first datasheet to organize data</p>
          {!search && (
            <Button onClick={() => { setSheetName(''); setIsCreateOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />New Datasheet
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((sheet) => (
            <div
              key={sheet.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/datasheets/${sheet.id}`)}
              data-testid={`datasheet-card-${sheet.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Table2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{sheet.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{sheet.columns?.length || 0} columns</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedSheet(sheet); setSheetName(sheet.name); setIsRenameOpen(true); }}>
                        <Pencil className="w-4 h-4 mr-2" />Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteTarget(sheet)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Created {new Date(sheet.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="create-datasheet-dialog">
          <DialogHeader><DialogTitle>New Datasheet</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={sheetName} onChange={(e) => setSheetName(e.target.value)} placeholder="e.g. Q1 Budget" data-testid="datasheet-name-input" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting} data-testid="save-datasheet-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Datasheet</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={sheetName} onChange={(e) => setSheetName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRename()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameOpen(false)}>Cancel</Button>
            <Button onClick={handleRename} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Datasheet</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteTarget?.name}"? All rows will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
